import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronDown,
  CircleCheck,
  CircleX,
  ExternalLink,
  KeyRound,
  RefreshCw,
  Unplug,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../api";
import { timeUntil } from "../../lib/format";
import { actorName } from "../../lib/people";
import type { Tone } from "../../lib/status";
import { cn } from "../../lib/utils";
import { useFactory } from "../../store";
import type { McpServerView } from "../../types";
import { IntegrationLogo } from "../integration-logo";
import { ColorDot, StatusDot, ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Spinner } from "../ui/spinner";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

const AUTH_TONE: Record<McpServerView["connection"]["authStatus"], Tone> = {
  connected: "emerald",
  disconnected: "neutral",
  expired: "amber",
  error: "red",
};

export function ServerCard({ view }: { view: McpServerView }) {
  const { server, connection, usedBy } = view;
  const queryClient = useQueryClient();
  const { layout, agentById } = useFactory();
  const [token, setToken] = useState("");
  const [envText, setEnvText] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["mcp"] });
  const onError = (error: Error) => toast.error(`${server.name}: ${error.message}`);
  const toggle = useMutation({
    mutationFn: () => api.mcpEnable(server.id, !connection.enabled),
    onSuccess: () => {
      toast.success(`${server.name} ${connection.enabled ? "switched off" : "switched on"}`);
      refresh();
    },
    onError,
  });
  const saveToken = useMutation({
    mutationFn: () => api.mcpCredentials(server.id, { token }),
    onSuccess: () => {
      setToken("");
      toast.success(`${server.name} token saved`);
      refresh();
    },
    onError,
  });
  const saveEnv = useMutation({
    mutationFn: () => api.mcpCredentials(server.id, { env: parseEnv(envText) }),
    onSuccess: () => {
      setEnvText("");
      toast.success(`${server.name} environment saved`);
      refresh();
    },
    onError,
  });
  const disconnect = useMutation({
    mutationFn: () => api.mcpDisconnect(server.id),
    onSuccess: refresh,
    onError,
  });
  const test = useMutation({
    mutationFn: () => api.mcpTest(server.id),
    onSuccess: (result) => {
      const health = result.connection.lastHealth;
      if (health?.ok)
        toast.success(`${server.name} is healthy`, { description: `${health.toolCount ?? 0} tools` });
      else toast.error(`${server.name} test failed`, { description: health?.error });
      refresh();
    },
    onError,
  });
  const oauth = useMutation({
    mutationFn: () => api.mcpOauthStart(server.id),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError,
  });
  const setTools = useMutation({
    mutationFn: (tools: string[] | null) => api.mcpTools(server.id, tools),
    onSuccess: refresh,
    onError,
  });
  const setAttachments = useMutation({
    mutationFn: (att: { sections: string[]; agents: string[] }) => api.mcpAttachments(server.id, att),
    onSuccess: refresh,
    onError,
  });

  const connected = connection.authStatus === "connected";
  const allTools = connection.discoveredTools.map((t) => t.name);
  const allowed = connection.allowedTools;
  const health = connection.lastHealth;
  const tone = AUTH_TONE[connection.authStatus];
  const actor = actorName(connection.updatedBy);

  return (
    <Card className={cn("@container/server", connection.enabled ? undefined : "bg-muted/30")}>
      <CardHeader className="border-b">
        <CardTitle className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background">
            <IntegrationLogo id={server.id} className="size-5" />
          </span>
          <span className="min-w-0 truncate">{server.name}</span>
          <ToneBadge tone={tone} className="gap-1.5 capitalize">
            <StatusDot tone={tone} />
            {connection.authStatus}
          </ToneBadge>
        </CardTitle>
        <CardDescription className="line-clamp-2">{server.description}</CardDescription>
        <CardAction>
          <Switch
            checked={connection.enabled}
            onCheckedChange={() => toggle.mutate()}
            disabled={toggle.isPending}
            aria-label={`Enable ${server.name}`}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4">
        {(server.url || server.docsUrl) && (
          <div className="flex items-center gap-3 text-xs">
            {server.url && (
              <span className="min-w-0 truncate font-mono text-muted-foreground" title={server.url}>
                {server.url}
              </span>
            )}
            {server.docsUrl && (
              <a
                href={server.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex shrink-0 items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                <BookOpen className="size-3.5" /> Docs <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        )}

        {(server.auth.type === "bearer" || server.auth.type === "header") && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (token.trim()) saveToken.mutate();
            }}
          >
            <Input
              type="password"
              placeholder={connected ? "Replace token…" : "Paste API token"}
              aria-label={`${server.name} API token`}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
            />
            <Button
              type="submit"
              variant={connected ? "outline" : "default"}
              disabled={!token.trim() || saveToken.isPending}
            >
              <KeyRound /> Save
            </Button>
          </form>
        )}
        {server.transport === "stdio" && (
          <Field>
            <FieldLabel htmlFor={`${server.id}-env`}>Environment</FieldLabel>
            <div className="flex gap-2">
              <Textarea
                id={`${server.id}-env`}
                rows={2}
                value={envText}
                onChange={(e) => setEnvText(e.target.value)}
                className="font-mono text-xs"
                placeholder="KEY=value"
              />
              <Button
                variant="outline"
                onClick={() => saveEnv.mutate()}
                disabled={!envText.trim() || saveEnv.isPending}
              >
                Save
              </Button>
            </div>
            <FieldDescription>One KEY=value per line.</FieldDescription>
          </Field>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {server.auth.type === "oauth" && (
            <Button
              size="sm"
              variant={connected ? "outline" : "default"}
              onClick={() => oauth.mutate()}
              disabled={oauth.isPending}
            >
              {oauth.isPending ? <Spinner /> : <KeyRound />}
              {connected ? "Reconnect" : "Connect with OAuth"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => test.mutate()}
            disabled={test.isPending || (!connected && server.auth.type !== "none")}
          >
            {test.isPending ? <Spinner /> : <RefreshCw />}
            Test connection
          </Button>
          {connected && server.auth.type !== "none" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              <Unplug /> Disconnect
            </Button>
          )}
          {(connection.tokenExpiresAt || health) && (
            <span className="ml-auto inline-flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              {connection.tokenExpiresAt && (
                <span className="text-xs text-muted-foreground">
                  Token expires {timeUntil(connection.tokenExpiresAt)}
                </span>
              )}
              {health && (
                <span
                  className={cn(
                    "inline-flex min-w-0 items-center gap-1.5 text-xs",
                    health.ok ? "text-emerald-600 dark:text-emerald-400" : "text-destructive",
                  )}
                  title={health.error}
                >
                  {health.ok ? (
                    <CircleCheck className="size-3.5 shrink-0" />
                  ) : (
                    <CircleX className="size-3.5 shrink-0" />
                  )}
                  <span className="truncate">
                    {health.ok ? `${health.toolCount ?? 0} tools` : health.error}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    · <TimeAgo at={health.checkedAt} />
                  </span>
                </span>
              )}
            </span>
          )}
        </div>
        {connection.lastError && !health?.ok && (
          <p className="text-sm text-destructive">{connection.lastError}</p>
        )}

        {allTools.length > 0 && (
          <Collapsible>
            <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-lg border bg-background px-3 py-2 text-left text-sm font-medium hover:bg-muted/40">
              Tool allow-list
              <span className="font-normal text-muted-foreground">
                {allowed ? `${allowed.length} of ${allTools.length}` : `All ${allTools.length}`}
              </span>
              <ChevronDown className="ml-auto size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 grid max-h-56 grid-cols-1 gap-2 overflow-y-auto rounded-lg border p-3 @md/server:grid-cols-2">
                {allTools.map((name) => {
                  const checked = !allowed || allowed.includes(name);
                  const id = `${server.id}-tool-${name}`;
                  return (
                    <div key={name} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={checked}
                        onCheckedChange={(value) => {
                          const current = allowed ?? allTools;
                          const next = value ? [...current, name] : current.filter((t) => t !== name);
                          setTools.mutate(next.length === allTools.length ? null : next);
                        }}
                      />
                      <Label htmlFor={id} className="truncate font-mono text-xs font-normal">
                        {name}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <Separator />

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Requested by</p>
            <div className="flex flex-wrap gap-1.5">
              {usedBy.length === 0 && <span className="text-sm text-muted-foreground">No agents yet</span>}
              {usedBy.map((u) => {
                const agent = agentById.get(u.agentId);
                return (
                  <Badge
                    key={u.agentId}
                    variant="outline"
                    title={u.required ? "Required" : "Optional"}
                    className={cn("gap-1.5", u.required ? "font-medium" : "text-muted-foreground")}
                  >
                    {agent && <ColorDot color={agent.station.color} className="size-2 ring-0" />}
                    {agent?.name ?? u.agentId}
                  </Badge>
                );
              })}
            </div>
          </div>
          {layout && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Also attach to sections</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 @md/server:grid-cols-3">
                {layout.sections.map((section) => {
                  const on = connection.attachments.sections.includes(section.id);
                  const id = `${server.id}-section-${section.id}`;
                  return (
                    <div key={section.id} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={on}
                        onCheckedChange={(value) =>
                          setAttachments.mutate({
                            agents: connection.attachments.agents,
                            sections: value
                              ? [...connection.attachments.sections, section.id]
                              : connection.attachments.sections.filter((s) => s !== section.id),
                          })
                        }
                      />
                      <Label htmlFor={id} className="flex items-center gap-1.5 font-normal">
                        <ColorDot color={section.color} className="size-2 ring-0" />
                        {section.name}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      {connection.updatedAt && (
        <CardFooter className="text-xs text-muted-foreground" title={actor ?? undefined}>
          Updated <TimeAgo at={connection.updatedAt} />
          {actor ? ` by ${actor}` : ""}
        </CardFooter>
      )}
    </Card>
  );
}

function parseEnv(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const idx = line.indexOf("=");
    if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return out;
}
